import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  pointerWithin,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { OneLead } from "./OneLead";
import NumberFormat from "react-number-format";

const stageKey = (id) => "stage-" + id;
const leadKey = (id) => "lead-" + id;

// Card targets take precedence over their containing column.
const detectCollision = (args) => {
  const collisions = args.pointerCoordinates
    ? pointerWithin(args)
    : rectIntersection(args);
  const outcome = collisions.find((hit) =>
    String(hit.id).startsWith("commercial-"),
  );
  if (outcome) return [outcome];
  const card = collisions.find((hit) => String(hit.id).startsWith("lead-"));
  if (card) return [card];
  const column = collisions.find((hit) => String(hit.id).startsWith("stage-"));
  if (column && args.pointerCoordinates) {
    // Resolve gaps between cards to the nearest card instead of appending to the column.
    const cards = args.droppableContainers.filter(
      (container) =>
        String(container.id).startsWith("lead-") &&
        stageKey(container.data.current?.stageId) === column.id,
    );
    if (cards.length) {
      const point = args.pointerCoordinates;
      return closestCenter({
        ...args,
        droppableContainers: cards,
        collisionRect: {
          ...args.collisionRect,
          left: point.x,
          right: point.x,
          top: point.y,
          bottom: point.y,
          width: 0,
          height: 0,
        },
      }).slice(0, 1);
    }
  }
  return collisions;
};

function LeadCard({
  lead,
  stageId,
  openEditLead,
  suppressClick,
  insertion,
  saveState,
  retryMove,
}) {
  const disabled = Boolean(
    lead.estado_comercial && lead.estado_comercial !== "abierta",
  );
  const busy =
    saveState?.status === "saving" || saveState?.status === "pending";
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
    transform,
    transition,
  } = useSortable({
    id: leadKey(lead.id),
    data: { lead, stageId },
    disabled,
  });
  const label = lead.referencia || lead.contacto || "oportunidad";
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      role="group"
      tabIndex={-1}
      aria-label={label}
      aria-busy={busy}
      className="bg-white rounded-lg mt-2 border border-gray-100 relative"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.35 : 1,
        touchAction: "none",
        cursor: disabled ? "default" : isDragging ? "grabbing" : "grab",
      }}
      onPointerDownCapture={() => {
        suppressClick.current = false;
      }}
      onKeyDownCapture={(event) => {
        if (event.key === "Enter" || event.key === " ")
          suppressClick.current = false;
      }}
      onClick={(event) => {
        if (!busy && !suppressClick.current && !event.target.closest("a"))
          openEditLead(lead);
      }}
    >
      {insertion && (
        <div
          aria-hidden="true"
          className="absolute left-0 right-0 h-[3px] bg-purple-600 rounded z-30 pointer-events-none"
          style={insertion === "after" ? { bottom: -5 } : { top: -5 }}
        />
      )}
      <button
        type="button"
        className="block w-full bg-transparent border-0 p-0 text-left"
        style={{ cursor: "inherit" }}
        disabled={busy}
      >
        <OneLead item={lead} />
      </button>
      {!!lead.id_ppto && (
        <div className="text-start pb-2 ms-3">
          <Link
            to={"/ppto/" + lead.id_ppto}
            target="_blank"
            rel="noopener noreferrer"
            className="p-link-hitos text-[13px] me-4"
            draggable={false}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <i className="bi bi-link-45deg text-[14px]" />
            <span className="ms-1">
              Presupuesto Nro. #{lead.nro_ppto || ""}
            </span>
          </Link>
        </div>
      )}
      {saveState && (
        <div
          role="status"
          className="px-3 pb-2 text-xs text-left"
          style={{
            color: saveState.status === "error" ? "#b91c1c" : "#747274",
          }}
        >
          {saveState.status === "error" ? (
            <>
              <span>
                {saveState.conflict
                  ? "La oportunidad cambió en otro dispositivo."
                  : "Cambio sin guardar."}
              </span>
              <button
                type="button"
                className="ml-2 underline"
                title={saveState.message}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  retryMove(lead.id);
                }}
              >
                {saveState.conflict ? "Actualizar y reintentar" : "Reintentar"}
              </button>
            </>
          ) : saveState.status === "pending" ? (
            "Pendiente de guardar…"
          ) : (
            "Guardando…"
          )}
        </div>
      )}
    </div>
  );
}

function StageColumn({ stage, children, currency, dragging, disabled }) {
  const { setNodeRef, isOver } = useDroppable({
    id: stageKey(stage.id),
    disabled,
  });
  return (
    <section
      ref={setNodeRef}
      aria-label={"Etapa " + stage.nombre}
      className="md:mt-0 max-sm:mt-5 w-full min-w-[267px] bg-[#fcf1ff] rounded-lg border mr-[10px] overflow-y-auto h-dvh p-noscroll"
      style={{
        borderColor: isOver ? "#9333ea" : "#f5e2fa",
        boxShadow: isOver ? "inset 0 0 0 2px #9333ea" : undefined,
      }}
    >
      <div className="p-2 text-start flex flex-col mb-2 border-b text-[16px] text-[#595759] sticky top-0 bg-[#fcf1ff] z-20">
        <span>
          <span className="font-bold lowercase first-letter:uppercase">
            {stage.nombre}
          </span>
          {stage.tipo && (
            <span
              className={
                "capitalize ms-2 " +
                (stage.tipo === "ganado" ? "text-green-600" : "text-red-600")
              }
            >
              ({stage.tipo})
            </span>
          )}
        </span>
        <span className="text-[13px] mt-1 text-[#747274]">
          <NumberFormat
            displayType="text"
            value={stage.suma_ingreso || 0}
            thousandSeparator={currency.separacion_miles}
            decimalSeparator={currency.separacion_decimales}
            prefix={currency.simbolo + " "}
            decimalScale={2}
            fixedDecimalScale
          />
          {" - "}
          {stage.cant_deals || 0} {stage.cant_deals === 1 ? "deal" : "deals"}
        </span>
      </div>
      <div className="px-2 pb-3 min-h-[120px]">
        {children}
        {stage.leads.length === 0 && (
          <p className="mt-3 text-gray-500 text-sm">
            {dragging
              ? "Suelta aquí la oportunidad"
              : stage.descripcion || "Sin oportunidades"}
          </p>
        )}
      </div>
    </section>
  );
}

function CommercialDropZone({ status, label }) {
  const { setNodeRef, isOver } = useDroppable({ id: `commercial-${status}` });
  return (
    <div
      ref={setNodeRef}
      role="region"
      aria-label={`Soltar como ${label.toLowerCase()}`}
      className={`flex-1 rounded-xl border-2 border-dashed p-4 text-center font-semibold shadow-lg ${status === "ganada" ? "bg-green-50 border-green-500 text-green-700" : "bg-red-50 border-red-500 text-red-700"} ${isOver ? "ring-2 ring-purple-500" : ""}`}
    >
      {label}
      <span className="block text-xs font-normal mt-1">
        Suelta aquí para cerrar
      </span>
    </div>
  );
}

export const Board = ({
  leads,
  openEditLead,
  moveLead,
  reorderLead,
  savingLeads = {},
  retryMove,
  summarySymbol,
  onCommercialChange,
}) => {
  const {
    local_simbolo: localSymbol,
    local_separacion_miles: separacion_miles,
    local_separacion_decimales: separacion_decimales,
  } = useSelector((state) => state.general.info);
  const simbolo = summarySymbol ?? localSymbol;
  const [active, setActive] = useState(null);
  const [insertion, setInsertion] = useState(null);
  const suppressClick = useRef(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );
  const stages = Array.isArray(leads) ? leads : [];
  const getDropPosition = ({ active: item, over, activatorEvent, delta }) => {
    if (!over) return null;
    const destination = stages.find(
      (stage) =>
        stageKey(stage.id) === over.id ||
        stage.leads.some((lead) => leadKey(lead.id) === over.id),
    );
    if (!destination) return null;
    const targetIndex = destination.leads.findIndex(
      (lead) => leadKey(lead.id) === over.id,
    );
    const pointerY =
      typeof activatorEvent?.clientY === "number"
        ? activatorEvent.clientY + (delta?.y || 0)
        : (item.rect?.current?.translated?.top || 0) +
          (item.rect?.current?.translated?.height || 0) / 2;
    const after =
      targetIndex >= 0 && over.rect
        ? pointerY > over.rect.top + over.rect.height / 2
        : false;
    return {
      destination,
      targetId: destination.leads[targetIndex]?.id,
      after,
      index:
        targetIndex < 0
          ? destination.leads.length
          : targetIndex + (after ? 1 : 0),
    };
  };
  return (
    <div className="mt-[20px]">
      <p
        role="status"
        aria-live="polite"
        className="text-sm text-gray-500 mb-2"
      >
        Arrastra una tarjeta para cambiarla de etapa o reordenarla en su columna
      </p>
      <DndContext
        sensors={sensors}
        collisionDetection={detectCollision}
        onDragStart={({ active: item }) => {
          suppressClick.current = true;
          setActive({
            ...item.data.current,
            width: item.rect?.current?.initial?.width,
          });
        }}
        onDragMove={(event) => {
          const position = getDropPosition(event);
          setInsertion(
            position &&
              String(position.destination.id) !==
                String(event.active.data.current.stageId)
              ? position
              : null,
          );
        }}
        onDragCancel={() => {
          setActive(null);
          setInsertion(null);
        }}
        onDragEnd={(event) => {
          const { active: item, over } = event;
          setActive(null);
          setInsertion(null);
          if (!over) return;
          if (String(over.id).startsWith("commercial-")) {
            onCommercialChange?.(
              item.data.current.lead,
              String(over.id).replace("commercial-", ""),
            );
            return;
          }
          const position = getDropPosition(event);
          if (!position) return;
          const { destination } = position;
          const { lead, stageId } = item.data.current;
          if (String(stageId) === String(destination.id)) {
            const target = destination.leads.find(
              (lead) => leadKey(lead.id) === over.id,
            );
            if (target && String(target.id) !== String(lead.id))
              reorderLead(lead.id, target.id, destination.id);
          } else moveLead(lead, destination.id, position.index);
        }}
      >
        <div className="text-center bg-white p-2 rounded-lg table-responsive">
          <div className="flex flex-row">
            {stages.map((stage) => (
              <StageColumn
                key={stage.id}
                stage={stage}
                currency={{ simbolo, separacion_miles, separacion_decimales }}
                dragging={!!active}
                disabled={["ganado", "perdido"].includes(stage.tipo)}
              >
                <SortableContext
                  items={stage.leads.map((lead) => leadKey(lead.id))}
                  strategy={verticalListSortingStrategy}
                >
                  {stage.leads.map((lead) => (
                    <LeadCard
                      key={lead.id}
                      lead={lead}
                      stageId={stage.id}
                      openEditLead={openEditLead}
                      suppressClick={suppressClick}
                      saveState={savingLeads[lead.id]}
                      retryMove={retryMove}
                      insertion={
                        insertion &&
                        String(insertion.destination.id) === String(stage.id) &&
                        String(insertion.targetId) === String(lead.id)
                          ? insertion.after
                            ? "after"
                            : "before"
                          : null
                      }
                    />
                  ))}
                </SortableContext>
              </StageColumn>
            ))}
          </div>
        </div>
        {active && onCommercialChange && (
          <div
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex gap-3"
            style={{ width: "calc(100% - 32px)", maxWidth: 520 }}
          >
            <CommercialDropZone status="ganada" label="Ganada" />
            <CommercialDropZone status="perdida" label="Perdida" />
          </div>
        )}
        <DragOverlay dropAnimation={null}>
          {active ? (
            <div
              className="bg-white rounded-lg shadow-xl border border-purple-300"
              style={{ width: active.width || 250, cursor: "grabbing" }}
            >
              <OneLead item={active.lead} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};
